import { useState, useEffect } from "react";
import { Coverage } from "../../../../models/offers";
import toast from "react-hot-toast";
import { error } from "console";

const STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
    "Washington", "West Virginia", "Wisconsin", "Wyoming", "District of Columbia"
];

type CoverageSelectorProps = {
    editedOffer: { coverage?: Coverage } | null;
    handleChange: (field: "coverage", value: Coverage) => void;
    setZipListFile: (file: File | null) => void;
};

export default function CoverageSelector({ editedOffer, handleChange, setZipListFile }: CoverageSelectorProps) {
    const [selectedStates, setSelectedStates] = useState<string[]>([]);
    const [listType, setListType] = useState<string>("");
    const [zipCodes, setZipCodes] = useState<string[]>([]);

    useEffect(() => {
        if (!editedOffer?.coverage) return;

        if (editedOffer.coverage.state_list) {
            if (editedOffer.coverage.state_list[0] === "Nationwide") {
                setSelectedStates([...STATES]);
            } else {
                setSelectedStates(editedOffer.coverage.state_list);
            }
            setListType("State List");
        } else if (editedOffer.coverage.number_of_zip_codes !== undefined) {
            setListType("Zip Codes");
        }
    }, [editedOffer?.coverage]);

    const allSelected = selectedStates.length === STATES.length;

    const toggleState = (state: string) => {
        const newStates = selectedStates.includes(state)
            ? selectedStates.filter(s => s !== state)
            : [...selectedStates, state];

        setSelectedStates(newStates);
        handleChange(
            "coverage",
            newStates.length === STATES.length
                ? { state_list: ["Nationwide"] }
                : { state_list: newStates }
        );
    };

    const toggleNationwide = () => {
        if (allSelected) {
            setSelectedStates([]);
            handleChange("coverage", { state_list: [] });
        } else {
            setSelectedStates([...STATES]);
            handleChange("coverage", { state_list: ["Nationwide"] });
        }
    };

    const handleZipFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const file = e.target.files?.[0];
            if (!file) return;

            setZipListFile(file);

            const text = await file.text();
            const zips = text
                .split(/[\n,]/)
                .map(z => z.trim())
                .filter(z => /^\d{5}$/.test(z));

            setZipCodes(zips);

            if (zips.length === 0) {
                throw new Error("No ZIP codes found in the file");
            }
            handleChange("coverage", {
                number_of_zip_codes: zips.length,
            });
        } catch (error) {
            setZipListFile(null);
            setZipCodes([]);
            e.target.value = "";
            toast.error("Failed to process zip file");
        }
    };

    return (
        <div className="flex flex-col space-y-2 max-h-60 overflow-y-auto border p-2 rounded">
            <select
                className="border p-1 rounded w-1/2"
                value={listType}
                onChange={(e) => {
                    const type = e.target.value;
                    setListType(type);
                    if (type === "State List") {
                        handleChange("coverage", { state_list: [] });
                        setSelectedStates([]);
                        setZipListFile(null);
                        setZipCodes([]);
                    } else if (type === "Zip Codes") {
                        handleChange("coverage", { number_of_zip_codes: 0 });
                    }
                }}
            >
                <option value="" disabled>Select Coverage type</option>
                <option value="State List">State List</option>
                <option value="Zip Codes">Zip Codes</option>
            </select>

            {listType === "State List" && (
                <>
                    <label className="flex items-center space-x-2">
                        <input type="checkbox" checked={allSelected} onChange={toggleNationwide} />
                        <span>Nationwide</span>
                    </label>
                    {STATES.map(st => (
                        <label key={st} className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={selectedStates.includes(st)}
                                onChange={() => toggleState(st)}
                            />
                            <span>{st}</span>
                        </label>
                    ))}
                </>
            )}

            {listType === "Zip Codes" && (
                <div className="space-y-2">
                    <input
                        type="file"
                        accept=".csv,.txt"
                        className="border p-1 rounded w-full"
                        onChange={handleZipFileUpload}
                    />
                    {zipCodes.length > 0 && (
                        <p className="text-sm text-gray-600">
                            Uploaded {zipCodes.length} ZIP codes
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
